<template>
  <article class="ai-turn" :class="`ai-turn--${message.role}`">
    <template v-if="message.role === 'assistant'">
      <div class="ai-turn__avatar ai-turn__avatar--tracey" aria-hidden="true">
        <v-icon small color="#fff">mdi-creation</v-icon>
      </div>
      <div class="ai-turn__col">
        <!-- Planning / thinking preamble kept as its own bubble -->
        <div v-if="message.thinking" class="ai-turn__bubble ai-turn__bubble--thinking">
          <div class="ai-turn__thinking-label">
            <v-icon size="13" color="#8e8ea0">mdi-lightbulb-outline</v-icon>
            <span>Planning</span>
          </div>
          <div class="ai-turn__text ai-prose ai-prose--thinking" v-html="parsedThinking" />
        </div>

        <div class="ai-turn__bubble" :class="{ 'ai-turn__bubble--error': message.isError }">
          <header v-if="formattedTimestamp" class="ai-turn__meta">{{ formattedTimestamp }}</header>

          <div v-if="assistantToolSteps.length" class="ai-turn__tools">
            <button
              type="button"
              class="ai-turn__tools-toggle"
              :aria-expanded="String(toolsExpanded)"
              :aria-label="toolsExpanded ? 'Hide tools used' : `Show ${assistantToolSteps.length} tools used`"
              :title="toolsExpanded ? 'Hide tools' : 'Show tools used'"
              @click="toolsExpanded = !toolsExpanded"
            >
              <v-icon size="16" color="#6e6e80">mdi-tools</v-icon>
              <span class="ai-turn__tools-count">{{ assistantToolSteps.length }}</span>
              <v-icon size="15" color="#6e6e80">{{ toolsExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            </button>
            <div v-if="toolsExpanded" class="ai-turn__tools-list">
              <div v-for="(step, i) in assistantToolSteps" :key="i" class="ai-turn__tool-step">
                <v-icon size="14" color="#22a06b">mdi-check-circle</v-icon>
                <span>{{ step.label }}</span>
              </div>
            </div>
          </div>

          <div class="ai-turn__text ai-prose" v-html="parsedContent" />

          <div class="ai-turn__actions">
            <v-btn
              v-if="message.isError"
              small
              depressed
              class="ai-turn__action-btn"
              color="primary"
              @click="$emit('retry')"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Retry
            </v-btn>
            <v-btn
              v-if="hasViewData"
              small
              depressed
              class="ai-turn__action-btn"
              color="primary"
              @click="$emit('view-data')"
            >
              View Data
            </v-btn>
            <v-btn
              v-if="fileAttachment"
              small
              outlined
              class="ai-turn__action-btn"
              color="primary"
              :loading="downloading"
              @click="onDownload"
            >
              <v-icon left small>mdi-file-pdf-box</v-icon>
              Download PDF
            </v-btn>
            <button
              v-if="hasCopyableText && !message.isError"
              type="button"
              class="ai-turn__copy"
              :title="copied ? 'Copied' : 'Copy response'"
              :aria-label="copied ? 'Copied' : 'Copy response'"
              @click="copyMessage"
            >
              <v-icon size="15">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              <span>{{ copied ? 'Copied' : 'Copy' }}</span>
            </button>
          </div>
          <div v-if="downloadError" class="ai-turn__download-error">{{ downloadError }}</div>
        </div>
      </div>
    </template>

    <div v-else class="ai-turn__bubble ai-turn__bubble--user">
      <header v-if="formattedTimestamp" class="ai-turn__meta">{{ formattedTimestamp }}</header>
      <div class="ai-turn__text">{{ displayContent }}</div>
    </div>
  </article>
</template>

<script>
import { parseAssistantMarkdown } from './markdownUtils.js';
import { describeTool } from './toolDescriptions.js';
import { downloadTraceyFile } from '../../APIManager';

export default {
  name: 'TraceyAiMessageRow',
  props: {
    message: {
      type: Object,
      required: true,
      validator: (m) => ['user', 'assistant'].includes(m.role),
    },
    isQueryDataPresent: {
      type: Function,
      required: true,
    },
    formatTimestamp: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      downloading: false,
      downloadError: '',
      toolsExpanded: false,
      copied: false,
      copyResetTimer: null,
    };
  },
  beforeDestroy() {
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
  },
  methods: {
    async onDownload() {
      const attachment = this.fileAttachment;
      if (!attachment || this.downloading) return;
      this.downloading = true;
      this.downloadError = '';
      try {
        await downloadTraceyFile(attachment.downloadUrl, attachment.filename);
      } catch (e) {
        this.downloadError = 'That download link has expired. Ask Tracey to regenerate the PDF.';
        console.error('Tracey PDF download failed:', e);
      } finally {
        this.downloading = false;
      }
    },
    async copyMessage() {
      const text = this.message.content ?? this.message.response ?? '';
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          document.body.removeChild(ta);
          return;
        }
        document.body.removeChild(ta);
      }
      this.copied = true;
      if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => {
        this.copied = false;
      }, 1800);
    },
  },
  computed: {
    displayContent() {
      return this.message.content ?? this.message.userInput ?? '';
    },
    parsedContent() {
      const content = this.message.content ?? this.message.response ?? '';
      return parseAssistantMarkdown(content);
    },
    parsedThinking() {
      return parseAssistantMarkdown(this.message.thinking || '');
    },
    formattedTimestamp() {
      return this.formatTimestamp(this.message.createdAt);
    },
    hasViewData() {
      return this.isQueryDataPresent(this.message);
    },
    hasCopyableText() {
      return !!(this.message.content ?? this.message.response ?? '');
    },
    fileAttachment() {
      return this.message.fileAttachment || null;
    },
    /** Completed tool calls for this assistant turn, de-duplicated, human-labelled. */
    assistantToolSteps() {
      if (this.message.role !== 'assistant') return [];
      const calls = this.message.toolCalls || [];
      const seen = new Set();
      const steps = [];
      calls.forEach((c) => {
        const label = describeTool(c && c.name);
        if (!seen.has(label)) {
          seen.add(label);
          steps.push({ label });
        }
      });
      return steps;
    },
  },
};
</script>

<style scoped>
.ai-turn {
  display: flex;
  gap: 12px;
  padding: 8px 24px;
  width: 100%;
  max-width: 88rem;
  margin: 0 auto;
  align-items: flex-start;
}

.ai-turn--assistant {
  justify-content: flex-start;
}

.ai-turn--user {
  justify-content: flex-end;
}

.ai-turn__avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.ai-turn__avatar--tracey {
  background: linear-gradient(135deg, #12253a 0%, #1e3a5f 100%);
}

.ai-turn__col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
}

/* Bubbles */
.ai-turn__bubble {
  min-width: 0;
  max-width: 100%;
  padding: 12px 16px;
  border-radius: 16px;
  border-top-left-radius: 4px;
  background: #f7f9fc;
  border: 1px solid #e6ecf3;
  color: #353740;
}

.ai-turn__bubble--thinking {
  background: #f0f1f4;
  border: 1px dashed #d7dae2;
}

.ai-turn__bubble--user {
  max-width: 58rem;
  background: #e6f0f5;
  border: 1px solid rgba(18, 37, 58, 0.08);
  border-top-left-radius: 16px;
  border-top-right-radius: 4px;
  color: #12253a;
}

.ai-turn__bubble--error {
  background: #fff5f5;
  border-color: #f7c7c7;
}

.ai-turn__bubble--error .ai-turn__text {
  color: #b42318;
}

.ai-turn__thinking-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8e8ea0;
  margin-bottom: 6px;
}

.ai-turn__meta {
  font-size: 11px;
  color: #8e8ea0;
  margin-bottom: 6px;
}

.ai-turn__text {
  font-size: 15px;
  line-height: 1.65;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.ai-turn__bubble--user .ai-turn__text {
  white-space: pre-wrap;
}

.ai-prose--thinking {
  font-size: 13.5px;
  color: #6e6e80;
}

/* Tools (collapsed) */
.ai-turn__tools {
  margin-bottom: 10px;
}

.ai-turn__tools-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px 2px 4px;
  border-radius: 6px;
  font-size: 13px;
  color: #6e6e80;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.ai-turn__tools-toggle:hover {
  background-color: #eef1f6;
  color: #353740;
}

.ai-turn__tools-count {
  font-size: 12px;
  font-weight: 600;
  color: #6e6e80;
}

.ai-turn__tools-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 6px 0 0 6px;
  padding-left: 8px;
  border-left: 2px solid #e6ecf3;
}

.ai-turn__tool-step {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #353740;
}

/* Actions row (assistant) */
.ai-turn__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.ai-turn__action-btn {
  text-transform: none;
  letter-spacing: 0;
}

.ai-turn__copy {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 13px;
  color: #6e6e80;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.ai-turn__copy:hover {
  background-color: #eef1f6;
  color: #353740;
}

.ai-turn__download-error {
  font-size: 11px;
  color: #d32f2f;
  margin-top: 6px;
}

/* Markdown (assistant) */
.ai-prose ::v-deep p {
  margin: 0 0 0.75em;
}

.ai-prose ::v-deep p:last-child {
  margin-bottom: 0;
}

/* Links in Tracey replies (tenders, buyers, suppliers). !important is needed
   because the global customstyles.css forces every <a> to navy with
   !important, which made links indistinguishable from body text. Scoped to
   .ai-prose so the rest of the app keeps its link styling. */
.ai-prose ::v-deep a,
.ai-prose ::v-deep a:visited {
  color: #ff1463 !important;
  text-decoration: underline !important;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
  font-weight: 500;
  cursor: pointer;
}

.ai-prose ::v-deep a:hover,
.ai-prose ::v-deep a:focus-visible {
  color: #c80f4e !important;
  text-decoration-thickness: 2px;
}

.ai-prose ::v-deep img {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0.75em 0;
  border-radius: 8px;
}

.ai-prose ::v-deep pre,
.ai-prose ::v-deep code {
  max-width: 100%;
}

.ai-prose ::v-deep pre {
  overflow-x: auto;
  padding: 12px;
  border-radius: 8px;
  background: #eef1f6;
}

.ai-prose ::v-deep .ai-prose__table-wrap {
  overflow-x: auto;
  margin: 0.75em 0;
  max-width: 100%;
  width: 100%;
}

.ai-prose ::v-deep table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 13px;
}

.ai-prose ::v-deep th,
.ai-prose ::v-deep td {
  padding: 8px 12px;
  border: 1px solid #e6ecf3;
  text-align: left;
  max-width: none;
  white-space: normal;
}

.ai-prose ::v-deep th {
  background: #eef1f6;
}
</style>

<!--
  Non-scoped: hideEmptyTableColumns() (markdownUtils.js) tags fully-empty columns
  with a hide-col-N class; these rules hide the Nth column for any .ai-prose table,
  so both the message row and the streaming row drop empty columns from wide tables.
-->
<style>
.ai-prose table.hide-col-1 th:nth-child(1), .ai-prose table.hide-col-1 td:nth-child(1),
.ai-prose table.hide-col-2 th:nth-child(2), .ai-prose table.hide-col-2 td:nth-child(2),
.ai-prose table.hide-col-3 th:nth-child(3), .ai-prose table.hide-col-3 td:nth-child(3),
.ai-prose table.hide-col-4 th:nth-child(4), .ai-prose table.hide-col-4 td:nth-child(4),
.ai-prose table.hide-col-5 th:nth-child(5), .ai-prose table.hide-col-5 td:nth-child(5),
.ai-prose table.hide-col-6 th:nth-child(6), .ai-prose table.hide-col-6 td:nth-child(6),
.ai-prose table.hide-col-7 th:nth-child(7), .ai-prose table.hide-col-7 td:nth-child(7),
.ai-prose table.hide-col-8 th:nth-child(8), .ai-prose table.hide-col-8 td:nth-child(8),
.ai-prose table.hide-col-9 th:nth-child(9), .ai-prose table.hide-col-9 td:nth-child(9),
.ai-prose table.hide-col-10 th:nth-child(10), .ai-prose table.hide-col-10 td:nth-child(10),
.ai-prose table.hide-col-11 th:nth-child(11), .ai-prose table.hide-col-11 td:nth-child(11),
.ai-prose table.hide-col-12 th:nth-child(12), .ai-prose table.hide-col-12 td:nth-child(12),
.ai-prose table.hide-col-13 th:nth-child(13), .ai-prose table.hide-col-13 td:nth-child(13),
.ai-prose table.hide-col-14 th:nth-child(14), .ai-prose table.hide-col-14 td:nth-child(14),
.ai-prose table.hide-col-15 th:nth-child(15), .ai-prose table.hide-col-15 td:nth-child(15),
.ai-prose table.hide-col-16 th:nth-child(16), .ai-prose table.hide-col-16 td:nth-child(16),
.ai-prose table.hide-col-17 th:nth-child(17), .ai-prose table.hide-col-17 td:nth-child(17),
.ai-prose table.hide-col-18 th:nth-child(18), .ai-prose table.hide-col-18 td:nth-child(18),
.ai-prose table.hide-col-19 th:nth-child(19), .ai-prose table.hide-col-19 td:nth-child(19),
.ai-prose table.hide-col-20 th:nth-child(20), .ai-prose table.hide-col-20 td:nth-child(20) {
  display: none;
}

/* Click-to-play YouTube card (built by embedYouTube in markdownUtils) */
.ai-prose .ai-video {
  position: relative;
  display: block;
  width: 100%;
  max-width: 560px;
  margin: 12px 0;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 2px 12px rgba(18, 37, 58, 0.18);
  text-decoration: none;
}

.ai-prose .ai-video__thumb {
  display: block;
  width: 100%;
  height: auto;
}

.ai-prose .ai-video__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-prose .ai-video__play-bg {
  fill: #ff0000;
  opacity: 0.9;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.ai-prose .ai-video:hover .ai-video__play-bg {
  opacity: 1;
}

.ai-prose .ai-video:hover .ai-video__play svg {
  transform: scale(1.06);
}

.ai-prose .ai-video__play svg {
  transition: transform 0.15s ease;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4));
}
</style>
